<template>
  <BaseModal
    :title="$t('article_put_online_details.ai_photo_modal.title')"
    :subtitle="$t('article_put_online_details.ai_photo_modal.subtitle')"
    cancelText=""
    submitText=""
    @close="$emit('close')"
  >
    <template #content>
      <div class="ai-photo-modal">
        <div v-if="creditBalance !== null" class="ai-photo-modal__credits">
          {{
            $t("article_put_online_details.ai_photo_modal.credits_balance", {
              balance: creditBalance,
            })
          }}
        </div>

        <div class="ai-photo-modal__actions">
          <Button
            color="black"
            size="m"
            :loading="generateLoading"
            :disabled="isActionDisabled"
            @click.native.prevent="handleGenerate"
          >
            {{ $t("article_put_online_details.ai_photo_modal.generate") }}
          </Button>
        </div>

        <InPopinLoader
          v-if="listLoading"
          :loading="true"
          :msg="$t('article_put_online_details.ai_photo_modal.loading')"
        />

        <p v-else-if="!requests.length" class="ai-photo-modal__empty">
          {{ $t("article_put_online_details.ai_photo_modal.empty") }}
        </p>

        <div v-else class="ai-photo-modal__grid">
          <div
            v-for="request in sortedRequests"
            :key="request.id"
            class="ai-photo-modal__item"
          >
            <div
              v-if="request.resultImageUrl"
              class="ai-photo-modal__preview ai-photo-modal__preview--zoomable"
              @click="openZoom(request.resultImageUrl)"
            >
              <img
                :src="request.resultImageUrl"
                :alt="request.id"
              />
              <span class="ai-photo-modal__zoom-hint" aria-hidden="true">
                <SvgIcon name="search" />
              </span>
            </div>
            <div v-else class="ai-photo-modal__preview">
              <div class="ai-photo-modal__placeholder">
                <SvgLoader
                  v-if="isInProgress(request)"
                  class="ai-photo-modal__loader"
                />
                <span>{{ statusLabel(request.status) }}</span>
              </div>
            </div>

            <div class="ai-photo-modal__meta">
              <div class="ai-photo-modal__meta-row">
                <span
                  class="ai-photo-modal__status"
                  :class="`ai-photo-modal__status--${request.status}`"
                >
                  {{ statusLabel(request.status) }}
                </span>
                <CustomTooltip
                  interactive
                  :title="$t('article_put_online_details.ai_photo_modal.info_title')"
                >
                  <button
                    type="button"
                    class="ai-photo-modal__prompt-info"
                    :aria-label="$t('article_put_online_details.ai_photo_modal.info_title')"
                  >
                    <SvgIcon name="info" />
                  </button>
                  <template #content>
                    <div class="ai-photo-modal__info">
                      <p class="ai-photo-modal__info-row">
                        <strong>{{ requestTypeLabel(request) }}</strong>
                      </p>
                      <div class="ai-photo-modal__info-row ai-photo-modal__info-source">
                        <span class="ai-photo-modal__info-label">
                          {{ $t("article_put_online_details.ai_photo_modal.info_source") }} :
                        </span>
                        <img
                          v-if="sourceImageUrl(request)"
                          :src="sourceImageUrl(request)"
                          :alt="sourceLabel(request)"
                          class="ai-photo-modal__info-source-image"
                        />
                      </div>
                      <p
                        v-if="displayedSystemPrompt(request)"
                        class="ai-photo-modal__info-row"
                      >
                        <span class="ai-photo-modal__info-label">
                          {{ $t("article_put_online_details.ai_photo_modal.info_prompt_system") }} :
                        </span>
                        {{ displayedSystemPrompt(request) }}
                      </p>
                      <p
                        v-if="displayedCompiledPrompt(request)"
                        class="ai-photo-modal__info-row"
                      >
                        <span class="ai-photo-modal__info-label">
                          {{ $t("article_put_online_details.ai_photo_modal.info_prompt_compiled") }} :
                        </span>
                        {{ displayedCompiledPrompt(request) }}
                      </p>
                      <p
                        v-if="hasGenerationProvider(request)"
                        class="ai-photo-modal__info-row"
                      >
                        <span class="ai-photo-modal__info-label">
                          {{ $t("article_put_online_details.ai_photo_modal.info_generation_provider") }} :
                        </span>
                        {{ generationProviderLabel(request) }}
                      </p>
                      <p
                        v-if="hasFidelityScore(request)"
                        class="ai-photo-modal__info-row"
                      >
                        <span class="ai-photo-modal__info-label">
                          {{ $t("article_put_online_details.ai_photo_modal.info_fidelity_score") }} :
                        </span>
                        {{ formatFidelityScore(request.fidelityScore) }}
                      </p>
                      <p
                        v-if="hasFidelityAttempts(request)"
                        class="ai-photo-modal__info-row"
                      >
                        <span class="ai-photo-modal__info-label">
                          {{ $t("article_put_online_details.ai_photo_modal.info_fidelity_attempts") }} :
                        </span>
                        {{ request.fidelityAttempts }}
                      </p>
                      <p
                        v-if="request.userPrompt"
                        class="ai-photo-modal__info-row"
                      >
                        <span class="ai-photo-modal__info-label">
                          {{ $t("article_put_online_details.ai_photo_modal.info_prompt_user") }} :
                        </span>
                        {{ request.userPrompt }}
                      </p>
                    </div>
                  </template>
                </CustomTooltip>
              </div>
              <span class="ai-photo-modal__date">
                {{ formatDate(request.createdAt) }}
              </span>
            </div>

            <Button
              v-if="request.status === 'completed'"
              color="grey"
              size="s"
              class="ai-photo-modal__apply"
              :loading="applyingRequestId === request.id"
              :disabled="applyingRequestId === request.id || isActionDisabled"
              @click.native.prevent="handleApply(request)"
            >
              {{ $t("article_put_online_details.ai_photo_modal.apply") }}
            </Button>

            <Button
              v-if="canShowVisionQaAttempts(request)"
              color="grey"
              size="s"
              class="ai-photo-modal__attempts-toggle"
              :loading="attemptsDetailLoadingRequestId === request.id"
              :disabled="attemptsDetailLoadingRequestId === request.id"
              @click.native.prevent="openAttemptsDetail(request)"
            >
              {{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempts_button") }}
            </Button>
          </div>
        </div>

        <div
          v-if="attemptsDetailRequest"
          class="ai-photo-modal__attempts-overlay"
          @click.self="closeAttemptsDetail"
        >
          <div class="ai-photo-modal__attempts-panel" @click.stop>
            <div class="ai-photo-modal__attempts-header">
              <h3 class="ai-photo-modal__attempts-title">
                {{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempts_title") }}
              </h3>
              <button
                type="button"
                class="ai-photo-modal__attempts-close"
                :aria-label="$t('article_put_online_details.ai_photo_modal.vision_qa_attempts_close')"
                @click="closeAttemptsDetail"
              >
                <SvgIcon name="cross" />
              </button>
            </div>

            <p v-if="!hasVisionQaAttemptsList(attemptsDetailRequest)" class="ai-photo-modal__attempts-empty">
              {{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempts_empty") }}
            </p>

            <div v-else class="ai-photo-modal__attempts-list">
              <div
                v-for="attempt in attemptsDetailRequest.visionQaAttempts"
                :key="`${attemptsDetailRequest.id}-${attempt.attemptNumber}`"
                class="ai-photo-modal__attempt-card"
              >
                <div class="ai-photo-modal__attempt-card-header">
                  <span class="ai-photo-modal__attempt-number">
                    {{
                      $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_number", {
                        number: attempt.attemptNumber,
                      })
                    }}
                  </span>
                  <span
                    class="ai-photo-modal__attempt-passed"
                    :class="attempt.passed
                      ? 'ai-photo-modal__attempt-passed--ok'
                      : 'ai-photo-modal__attempt-passed--ko'"
                  >
                    {{
                      attempt.passed
                        ? $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_passed")
                        : $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_failed")
                    }}
                  </span>
                </div>

                <div
                  v-if="attempt.generatedImageUrl"
                  class="ai-photo-modal__attempt-preview ai-photo-modal__preview--zoomable"
                  @click="openZoom(attempt.generatedImageUrl)"
                >
                  <img
                    :src="attempt.generatedImageUrl"
                    :alt="`Attempt ${attempt.attemptNumber}`"
                  />
                  <span class="ai-photo-modal__zoom-hint" aria-hidden="true">
                    <SvgIcon name="search" />
                  </span>
                </div>

                <dl class="ai-photo-modal__attempt-details">
                  <div class="ai-photo-modal__attempt-detail-row">
                    <dt>{{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_score") }}</dt>
                    <dd>{{ formatFidelityScore(attempt.score) }}</dd>
                  </div>
                  <div class="ai-photo-modal__attempt-detail-row">
                    <dt>{{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_min_score") }}</dt>
                    <dd>{{ formatFidelityScore(attempt.minScore) }}</dd>
                  </div>
                  <div v-if="attempt.caption" class="ai-photo-modal__attempt-detail-row">
                    <dt>{{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_caption") }}</dt>
                    <dd>{{ attempt.caption }}</dd>
                  </div>
                  <div v-if="attempt.metadata" class="ai-photo-modal__attempt-detail-row">
                    <dt>{{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_metadata") }}</dt>
                    <dd>
                      {{ formatAttemptMetadata(attempt.metadata) }}
                    </dd>
                  </div>
                  <div class="ai-photo-modal__attempt-detail-row">
                    <dt>{{ $t("article_put_online_details.ai_photo_modal.vision_qa_attempt_date") }}</dt>
                    <dd>{{ formatDate(attempt.createdAt) }}</dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="zoomedImageUrl"
          class="ai-photo-modal__zoom"
          @click.self="closeZoom"
        >
          <button
            type="button"
            class="ai-photo-modal__zoom-close"
            :aria-label="$t('article_put_online_details.ai_photo_modal.close_zoom')"
            @click="closeZoom"
          >
            <SvgIcon name="cross" />
          </button>

          <button
            v-if="zoomedImageIndex > 0"
            type="button"
            class="ai-photo-modal__zoom-nav ai-photo-modal__zoom-nav--prev"
            :aria-label="$t('article_put_online_details.ai_photo_modal.previous_photo')"
            @click.stop="showPreviousZoomedImage"
          >
            <SvgIcon name="left" />
          </button>

          <img
            :src="zoomedImageUrl"
            :alt="$t('article_put_online_details.ai_photo_modal.zoom_alt')"
            class="ai-photo-modal__zoom-image"
            @click.stop
          />

          <button
            v-if="zoomedImageIndex < zoomableImageUrls.length - 1"
            type="button"
            class="ai-photo-modal__zoom-nav ai-photo-modal__zoom-nav--next"
            :aria-label="$t('article_put_online_details.ai_photo_modal.next_photo')"
            @click.stop="showNextZoomedImage"
          >
            <SvgIcon name="right" />
          </button>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
import { mapActions } from "vuex";
import BaseModal from "@/components/ui/modals/BaseModal.vue";
import Button from "@/components/ui/button/Button.vue";
import InPopinLoader from "@/components/ui/popin/InPopinLoader.vue";
import SvgIcon from "@/components/ui/svg/SvgIcon.vue";
import SvgLoader from "@/components/ui/svg/SvgLoader.vue";
import CustomTooltip from "@/components/ui/tootip/CustomTooltip.vue";
import dateFormatMixin from "@/mixins/dateFormatMixin";

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 120;

export default {
  name: "ArticleCardAiPhotoModal",

  components: {
    BaseModal,
    Button,
    InPopinLoader,
    SvgIcon,
    SvgLoader,
    CustomTooltip,
  },

  mixins: [dateFormatMixin],

  props: {
    articleCardId: {
      type: String,
      required: true,
    },
    clientId: {
      type: [Number, String],
      required: true,
    },
    isFaumeAdmin: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      requests: [],
      creditBalance: null,
      listLoading: false,
      generateLoading: false,
      applyingRequestId: null,
      pollTimeoutId: null,
      zoomedImageUrl: null,
      zoomedImageIndex: 0,
      attemptsDetailRequest: null,
      attemptsDetailLoadingRequestId: null,
    };
  },

  computed: {
    zoomableImageUrls() {
      return this.sortedRequests
        .filter((request) => request.resultImageUrl)
        .map((request) => request.resultImageUrl);
    },

    sortedRequests() {
      return [...this.requests].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
    },

    hasInProgressRequest() {
      return this.requests.some((request) => this.isInProgress(request));
    },

    isActionDisabled() {
      return (
        this.generateLoading ||
        this.hasInProgressRequest ||
        this.listLoading
      );
    },
  },

  created() {
    this.loadModalData();
  },

  beforeDestroy() {
    this.clearPollTimeout();
    this.closeAttemptsDetail();
    document.body.classList.remove("no-scroll");
  },

  methods: {
    ...mapActions([
      "fetchFlatlayRequests",
      "createFlatlayGeneration",
      "fetchFlatlayRequest",
      "applyFlatlayRequest",
      "fetchAiPhotoCredits",
    ]),

    async loadModalData() {
      this.listLoading = true;
      try {
        const [requests, credits] = await Promise.all([
          this.fetchFlatlayRequests(this.articleCardId),
          this.fetchAiPhotoCredits(this.clientId),
        ]);
        this.requests = requests;
        this.creditBalance = credits.balance;
        this.resumePollingForInProgressRequests();
      } catch (error) {
        console.error("Erreur lors du chargement des photos IA:", error);
      } finally {
        this.listLoading = false;
      }
    },

    isInProgress(request) {
      return request.status === "pending" || request.status === "processing";
    },

    statusLabel(status) {
      const key = `article_put_online_details.ai_photo_modal.status.${status}`;
      return this.$te(key) ? this.$t(key) : status;
    },

    requestTypeLabel(request) {
      if (request.type === "retouch") {
        return this.$t("article_put_online_details.ai_photo_modal.info_type_retouch");
      }

      return this.$t("article_put_online_details.ai_photo_modal.info_type_generation");
    },

    sourceLabel(request) {
      const source = request.source || {};
      const kind = source.kind || "trade_in_photo";

      if (kind === "packshot_front") {
        return this.$t(
          "article_put_online_details.ai_photo_modal.info_source_packshot_front"
        );
      }

      if (kind === "packshot") {
        return this.$t(
          "article_put_online_details.ai_photo_modal.info_source_packshot",
          { position: source.position ?? "-" }
        );
      }

      if (kind === "ai_request_result") {
        return this.$t(
          "article_put_online_details.ai_photo_modal.info_source_ai_request_result",
          { id: source.parentRequestId || request.sourceRequestId || "-" }
        );
      }

      return this.$t(
        "article_put_online_details.ai_photo_modal.info_source_trade_in_photo"
      );
    },

    sourceImageUrl(request) {
      return request.source?.imageUrl || request.sourceImageUrl || null;
    },

    displayedSystemPrompt(request) {
      if (request.type === "retouch" || !this.isFaumeAdmin) {
        return null;
      }

      return request.aiPhotoPromptContent || null;
    },

    displayedCompiledPrompt(request) {
      if (request.type === "retouch" || !this.isFaumeAdmin) {
        return null;
      }

      return request.compiledPrompt || null;
    },

    hasFidelityScore(request) {
      return request.type === "generation" && request.fidelityScore !== null && request.fidelityScore !== undefined;
    },

    hasGenerationProvider(request) {
      return request.type === "generation" && Boolean(request.generationProvider);
    },

    generationProviderLabel(request) {
      const provider = request.generationProvider;
      const key = `article_put_online_details.ai_photo_modal.generation_provider_${provider}`;
      return this.$te(key) ? this.$t(key) : provider;
    },

    hasFidelityAttempts(request) {
      return request.type === "generation" && request.fidelityAttempts !== null && request.fidelityAttempts !== undefined;
    },

    formatFidelityScore(score) {
      const numericScore = Number(score);
      if (Number.isNaN(numericScore)) {
        return "-";
      }

      return `${Math.round(numericScore * 1000) / 10} %`;
    },

    canShowVisionQaAttempts(request) {
      return (
        this.isFaumeAdmin &&
        request.type === "generation" &&
        (request.status === "completed" ||
          request.status === "fidelity_rejected" ||
          request.status === "failed")
      );
    },

    hasVisionQaAttemptsList(request) {
      return Array.isArray(request?.visionQaAttempts) && request.visionQaAttempts.length > 0;
    },

    formatAttemptMetadata(metadata) {
      if (!metadata) {
        return "-";
      }

      const parts = [];
      if (metadata.width && metadata.height) {
        parts.push(`${metadata.width}×${metadata.height}`);
      }
      if (metadata.format) {
        parts.push(metadata.format);
      }
      if (metadata.sizeMB !== null && metadata.sizeMB !== undefined) {
        parts.push(`${metadata.sizeMB} MB`);
      }

      return parts.length ? parts.join(" · ") : "-";
    },

    async openAttemptsDetail(request) {
      this.attemptsDetailLoadingRequestId = request.id;
      try {
        const detail = await this.fetchFlatlayRequest({
          articleCardId: this.articleCardId,
          requestId: request.id,
        });
        this.attemptsDetailRequest = detail;
        document.body.classList.add("no-scroll");
      } catch (error) {
        console.error("Erreur lors du chargement des tentatives QA:", error);
        this.$store.commit("SET_NOTIFICATION_MESSAGE", {
          text: this.$t(
            "article_put_online_details.ai_photo_modal.vision_qa_attempts_load_error"
          ),
          color: "red",
          confirmation: true,
          duration: 4000,
        });
      } finally {
        this.attemptsDetailLoadingRequestId = null;
      }
    },

    closeAttemptsDetail() {
      this.attemptsDetailRequest = null;
      if (!this.zoomedImageUrl) {
        document.body.classList.remove("no-scroll");
      }
    },

    formatDate(value) {
      return value ? this.formatDateForDisplay(value) : "-";
    },

    openZoom(imageUrl) {
      const index = this.zoomableImageUrls.indexOf(imageUrl);
      this.zoomedImageIndex = index >= 0 ? index : 0;
      this.zoomedImageUrl = imageUrl;
      document.body.classList.add("no-scroll");
    },

    closeZoom() {
      this.zoomedImageUrl = null;
      this.zoomedImageIndex = 0;
      if (!this.attemptsDetailRequest) {
        document.body.classList.remove("no-scroll");
      }
    },

    showPreviousZoomedImage() {
      if (this.zoomedImageIndex <= 0) {
        return;
      }
      this.zoomedImageIndex -= 1;
      this.zoomedImageUrl = this.zoomableImageUrls[this.zoomedImageIndex];
    },

    showNextZoomedImage() {
      if (this.zoomedImageIndex >= this.zoomableImageUrls.length - 1) {
        return;
      }
      this.zoomedImageIndex += 1;
      this.zoomedImageUrl = this.zoomableImageUrls[this.zoomedImageIndex];
    },

    updateRequestInList(updatedRequest) {
      const index = this.requests.findIndex(
        (request) => request.id === updatedRequest.id
      );
      if (index === -1) {
        this.requests = [updatedRequest, ...this.requests];
        return;
      }
      this.$set(this.requests, index, updatedRequest);
    },

    clearPollTimeout() {
      if (this.pollTimeoutId !== null) {
        clearTimeout(this.pollTimeoutId);
        this.pollTimeoutId = null;
      }
    },

    pollRequestUntilDone(requestId) {
      return new Promise((resolve, reject) => {
        let attempts = 0;

        const poll = async () => {
          attempts += 1;
          try {
            const request = await this.fetchFlatlayRequest({
              articleCardId: this.articleCardId,
              requestId,
            });
            this.updateRequestInList(request);

            if (request.status === "completed" || request.status === "failed" || request.status === "fidelity_rejected") {
              resolve(request);
              return;
            }

            if (attempts >= POLL_MAX_ATTEMPTS) {
              reject(new Error("Polling timeout"));
              return;
            }

            this.pollTimeoutId = setTimeout(poll, POLL_INTERVAL_MS);
          } catch (error) {
            reject(error);
          }
        };

        poll();
      });
    },

    resumePollingForInProgressRequests() {
      const inProgress = this.requests.find((request) =>
        this.isInProgress(request)
      );
      if (!inProgress || this.generateLoading) {
        return;
      }

      this.generateLoading = true;
      this.pollRequestUntilDone(inProgress.id)
        .catch((error) => {
          console.error("Erreur lors du polling flatlay:", error);
        })
        .finally(() => {
          this.generateLoading = false;
        });
    },

    showRequestError(error, fallbackKey) {
      const status = error?.response?.status;
      const data = error?.response?.data || {};
      let message = data.message;

      if (status === 402) {
        message = this.$t(
          "article_put_online_details.ai_photo_modal.errors.insufficient_credits",
          {
            required: data.required,
            available: data.available,
          }
        );
      } else if (status === 409) {
        message = this.$t(
          "article_put_online_details.ai_photo_modal.errors.in_progress"
        );
        if (data.requestId) {
          this.loadModalData();
        }
      } else if (status === 422) {
        message =
          message ||
          this.$t(
            "article_put_online_details.ai_photo_modal.errors.missing_packshot"
          );
      } else if (!message) {
        message = this.$t(fallbackKey);
      }

      this.$store.commit("SET_NOTIFICATION_MESSAGE", {
        text: message,
        color: "red",
        confirmation: true,
        duration: 4000,
      });
    },

    async handleGenerate() {
      this.generateLoading = true;
      try {
        const createdRequest = await this.createFlatlayGeneration(
          this.articleCardId
        );
        this.updateRequestInList(createdRequest);
        await this.pollRequestUntilDone(createdRequest.id);
        const credits = await this.fetchAiPhotoCredits(this.clientId);
        this.creditBalance = credits.balance;
      } catch (error) {
        this.showRequestError(
          error,
          "article_put_online_details.ai_photo_modal.errors.generate_failed"
        );
      } finally {
        this.generateLoading = false;
      }
    },

    async handleApply(request) {
      this.applyingRequestId = request.id;
      try {
        await this.applyFlatlayRequest({
          articleCardId: this.articleCardId,
          requestId: request.id,
        });
        this.$emit("applied");
      } catch (error) {
        console.error("Erreur lors de l'application de la photo IA:", error);
      } finally {
        this.applyingRequestId = null;
      }
    },
  },
};
</script>

<style scoped>
.ai-photo-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-photo-modal__credits {
  font-size: 14px;
  color: #666;
  background: #f7f7f7;
  padding: 8px 12px;
  border-radius: 6px;
}

.ai-photo-modal__actions {
  display: flex;
  justify-content: flex-start;
}

.ai-photo-modal__empty {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.ai-photo-modal__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
}

.ai-photo-modal__item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
}

.ai-photo-modal__preview {
  aspect-ratio: 2 / 3;
  background: #f5f5f5;
  border-radius: 6px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-photo-modal__preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ai-photo-modal__preview--zoomable {
  position: relative;
  cursor: zoom-in;
}

.ai-photo-modal__preview--zoomable:hover .ai-photo-modal__zoom-hint {
  opacity: 1;
}

.ai-photo-modal__zoom-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 0.2s ease;
  --svg-color: #fff;
  pointer-events: none;
}

.ai-photo-modal__zoom-hint svg {
  width: 24px;
  height: 24px;
}

.ai-photo-modal__zoom {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.85);
}

.ai-photo-modal__zoom-image {
  max-width: min(90vw, 900px);
  max-height: 90vh;
  object-fit: contain;
}

.ai-photo-modal__zoom-close {
  position: absolute;
  top: 24px;
  right: 24px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  --svg-color: #fff;
}

.ai-photo-modal__zoom-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  --svg-color: #fff;
}

.ai-photo-modal__zoom-nav--prev {
  left: 24px;
}

.ai-photo-modal__zoom-nav--next {
  right: 24px;
}

.ai-photo-modal__placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: #666;
}

.ai-photo-modal__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ai-photo-modal__meta-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ai-photo-modal__prompt-info {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #f0f0f0;
  cursor: help;
  --svg-color: #666;
}

.ai-photo-modal__prompt-info:hover {
  background: #e8e8e8;
}

.ai-photo-modal__info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 320px;
}

.ai-photo-modal__info-row {
  margin: 0;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13px;
}

.ai-photo-modal__info-label {
  font-weight: 600;
}

.ai-photo-modal__info-source {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-photo-modal__info-source-image {
  display: block;
  width: 100%;
  max-width: 180px;
  max-height: 240px;
  object-fit: contain;
  border-radius: 6px;
  background: #f5f5f5;
}

.ai-photo-modal__date {
  font-size: 12px;
  color: #888;
}

.ai-photo-modal__status {
  display: inline-block;
  width: fit-content;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.ai-photo-modal__status--pending,
.ai-photo-modal__status--processing {
  background: #fff8e1;
  color: #f57c00;
}

.ai-photo-modal__status--completed {
  background: #e8f5e9;
  color: #2e7d32;
}

.ai-photo-modal__status--failed,
.ai-photo-modal__status--fidelity_rejected {
  background: #ffebee;
  color: #c62828;
}

.ai-photo-modal__apply,
.ai-photo-modal__attempts-toggle {
  width: 100%;
  justify-content: center;
}

.ai-photo-modal__attempts-overlay {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.55);
}

.ai-photo-modal__attempts-panel {
  width: min(720px, 100%);
  max-height: 85vh;
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-photo-modal__attempts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ai-photo-modal__attempts-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.ai-photo-modal__attempts-close {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: #f0f0f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  --svg-color: #666;
}

.ai-photo-modal__attempts-empty {
  margin: 0;
  color: #666;
  font-size: 14px;
}

.ai-photo-modal__attempts-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-photo-modal__attempt-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ai-photo-modal__attempt-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.ai-photo-modal__attempt-number {
  font-size: 14px;
  font-weight: 600;
}

.ai-photo-modal__attempt-passed {
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
}

.ai-photo-modal__attempt-passed--ok {
  background: #e8f5e9;
  color: #2e7d32;
}

.ai-photo-modal__attempt-passed--ko {
  background: #ffebee;
  color: #c62828;
}

.ai-photo-modal__attempt-preview {
  width: 100px;
  max-width: 100px;
  max-height: 140px;
  aspect-ratio: auto;
  align-self: flex-start;
}

.ai-photo-modal__attempt-preview img {
  max-height: 140px;
  object-fit: contain;
}

.ai-photo-modal__attempt-details {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ai-photo-modal__attempt-detail-row {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 8px;
  font-size: 13px;
}

.ai-photo-modal__attempt-detail-row dt {
  margin: 0;
  font-weight: 600;
  color: #555;
}

.ai-photo-modal__attempt-detail-row dd {
  margin: 0;
  word-break: break-word;
  white-space: pre-wrap;
}

.ai-photo-modal__loader {
  height: 24px;
  width: 24px;
}
</style>
